import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Eye, ArrowLeft, X } from 'lucide-react';
import TiptapEditor from './TiptapEditor';
import BlogLayout from './BlogLayout';
import useAdminPosts from '../../hooks/useAdminPosts';
import { useAuth } from '../../contexts/AuthContext';
import { extractExcerpt } from '../../utils/blogUtils';

const BlogEditor = ({ isMenuOpen, setIsMenuOpen }) => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    posts,
    createPost,
    updatePost,
    getPostById,
    loadAdminPosts,
    loading: postsLoading,
    error: postsError
  } = useAdminPosts();

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    tags: [],
    published: false
  });
  const [tagInput, setTagInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Check authentication and load admin posts only if needed
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/blog/admin/login');
      return;
    }
    
    // Only load admin posts if we don't have any posts yet and we have a session
    const sessionId = localStorage.getItem('adminSessionId');
    if (sessionId && posts.length === 0 && !postsLoading) {
      loadAdminPosts();
    }
  }, [isAuthenticated, navigate, posts.length, postsLoading]);

  // Load post data if editing
  useEffect(() => {
    if (postId && posts.length > 0) {
      const post = posts.find(p => p.id == postId);
      if (post) {
        setFormData({
          title: post.title,
          content: post.content,
          excerpt: post.excerpt,
          tags: post.tags,
          published: post.published
        });
        setIsEditing(true);
      } else {
        setError('Post not found');
      }
    }
  }, [postId, posts]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Auto-generate excerpt if content changes and excerpt is empty
      if (field === 'content' && !prev.excerpt) {
        newData.excerpt = extractExcerpt(value);
      }
      
      return newData;
    });
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSave = async (publishStatus) => {
    setIsLoading(true);
    setError('');

    const postData = {
      ...formData,
      published: publishStatus
    };

    try {
      let result;
      if (isEditing) {
        result = await updatePost(postId, postData);
      } else {
        result = await createPost(postData);
      }

      if (result.success) {
        navigate('/blog/admin/dashboard');
      } else {
        setError(result.errors.join(', '));
      }
    } catch (err) {
      setError('Failed to save post');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    // Open preview in new tab
    const previewData = {
      ...formData,
      id: 'preview',
      slug: 'preview',
      publishedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      author: 'Admin'
    };
    
    // Store preview data in sessionStorage
    sessionStorage.setItem('blogPreview', JSON.stringify(previewData));
    window.open('/blog/preview', '_blank');
  };

  if (!isAuthenticated) {
    return null;
  }

  if (postsLoading) {
    return (
      <BlogLayout 
        title="Loading Editor..." 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
      >
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout 
      title={'Admin Dashboard'}
      isMenuOpen={isMenuOpen} 
      setIsMenuOpen={setIsMenuOpen}
    >
      <div className="admin-container">
        {/* Header */}
        <div className="admin-header">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/blog/admin/dashboard')}
              className="admin-btn secondary"
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <h1 className="admin-title">
              {isEditing ? 'Edit' : 'New'}
            </h1>
          </div>
          
          <div className="admin-actions">
            <button
              onClick={() => handleSave(false)}
              className="admin-btn secondary"
              disabled={isLoading}
            >
              Save Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              className="admin-btn"
              disabled={isLoading}
            >
              <Save size={20} />
              {isLoading ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </div>

        {error && (
          <div className="form-error mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Editor Form */}
        <div className="blog-form">
          {/* Title */}
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="form-input"
              placeholder="Enter post title..."
              required
            />
          </div>

          {/* Content */}
          <div className="form-group">
            <label className="form-label">Content</label>
            <TiptapEditor
              content={formData.content}
              onChange={(value) => handleInputChange('content', value || '')}
            />
          </div>

          {/* Excerpt */}
          <div className="form-group">
            <label className="form-label">Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => handleInputChange('excerpt', e.target.value)}
              className="form-textarea"
              placeholder="Brief description of the post..."
              rows={3}
              required
            />
          </div>

          {/* Tags */}
          <div className="form-group">
            <label className="form-label">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                className="form-input flex-1"
                placeholder="Add a tag..."
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="admin-btn secondary"
              >
                Add
              </button>
            </div>
            
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="blog-tag flex items-center gap-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Published Status */}
          <div className="form-group">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => handleInputChange('published', e.target.checked)}
                className="w-4 h-4 text-indigo-600 bg-slate-700 border-slate-600 rounded focus:ring-indigo-500"
              />
              <span className="form-label mb-0">Publish</span>
            </label>
          </div>
        </div>
      </div>
    </BlogLayout>
  );
};

export default BlogEditor;